import { useEffect, useRef, useState } from 'react';
import type { DisplayState } from '~/components/ui/StatefulView';

type UseDisplayStateParams<T> = {
  isValidating: boolean;
  data: T[] | null | undefined; // Raw API data
  error: any;
  derivedData: T[]; // Data after search/filters
  isInitialLoadRef: React.MutableRefObject<boolean>; // Ref to track if it's the very first data loading cycle. The hook will set this to false after the first successful data load.
};

export function useDisplayState<T>({
  isValidating,
  data,
  error,
  derivedData,
  isInitialLoadRef,
}: UseDisplayStateParams<T>): DisplayState {
  const [currentDisplayState, setCurrentDisplayState] =
    useState<DisplayState>('INITIAL_LOADING');
  const noResultsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Helper function to set NO_RESULTS state after a delay
    const setNoResultsWithDelay = () => {
      noResultsTimeoutRef.current = setTimeout(() => {
        setCurrentDisplayState('NO_RESULTS');
      }, 100); // Consider if 100ms is always optimal
    };

    // Clear any pending timeout when dependencies change
    if (noResultsTimeoutRef.current) {
      clearTimeout(noResultsTimeoutRef.current);
      noResultsTimeoutRef.current = null;
    }

    if (isValidating) {
      setCurrentDisplayState(
        isInitialLoadRef.current ? 'INITIAL_LOADING' : 'LOADING'
      );
    } else {
      // Once validation is finished, it's no longer the "initial load" for subsequent renders triggered by this effect.
      // The hook manages setting isInitialLoadRef.current to false after the first successful IDLE state.

      if (error) {
        setCurrentDisplayState('ERROR');
        // Optional: console.error for debugging within the hook
        // console.error('Error detected in useDisplayState:', error);
      } else if (data === undefined || data === null) {
        // This condition means the API/data source has not provided any data array yet,
        // or explicitly returned null.
        setNoResultsWithDelay();
      } else if (derivedData.length === 0) {
        // Data from API might exist (data is an array, possibly empty or with items),
        // but after client-side processing (like search or filters), the list to display is empty.
        setNoResultsWithDelay();
      } else {
        // Data is present (not null/undefined) and derivedData also has items.
        setCurrentDisplayState('IDLE');
      }
    }

    // Cleanup function to clear timeout on unmount or before next effect run
    return () => {
      if (noResultsTimeoutRef.current) {
        clearTimeout(noResultsTimeoutRef.current);
        noResultsTimeoutRef.current = null;
      }
    };
  }, [isValidating, data, error, derivedData, isInitialLoadRef]);

  // This effect ensures that isInitialLoadRef.current is set to false
  // once the data has loaded successfully for the first time and the state is IDLE.
  // This helps differentiate initial loading from subsequent re-fetches or loading states.
  useEffect(() => {
    if (currentDisplayState === 'IDLE' && isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
    }
  }, [currentDisplayState, isInitialLoadRef]);

  return currentDisplayState;
}
